// Ring for new chat and email contacts.
//
// The agent workspace only plays its offer ring for contacts that are offered
// and must be accepted. CHAT and EMAIL are auto-accepted, so they skip the
// offer stage and nothing rings. The customer wants them to keep auto-accept
// and still ring. VOICE is excluded: the native workspace already rings for
// calls, and a second ring would double up.
//
// The app is registered PER_CONTACT, and the workspace only creates an
// instance for a contact when the agent selects it. Ringing from that instance
// rang on select, not on arrival. Instead every open instance watches the
// agent-wide listContacts() (see useConnectContact) and rings for a contact
// that appears after its first look. The first instance to claim the contact
// in localStorage (same origin across instances) plays. Every failure path is
// swallowed here, so the contact path never breaks.

// Imported through Vite so it ships hashed under assets/. deploy.sh only syncs
// assets/ plus root *.png/*.ico, so a public/ file would never reach S3 and the
// SPA fallback would serve index.html instead. Generated by
// scripts/generate_ring.py; swap the file or change this import.
import ringUrl from "./ring.wav";

export const RING_SRC: string = ringUrl;

const RING_CHANNELS: ReadonlySet<string> = new Set(["CHAT", "EMAIL"]);
const CLAIM_PREFIX = "cts-ring-claim:";
const CLAIM_TTL_MS = 24 * 60 * 60 * 1000;

// Contacts this instance has already rung for. The localStorage claim covers
// other instances; this covers repeat polls inside this one.
const ringedHere = new Set<string>();

/** Test hook: forget which contacts this instance has rung for. */
export function __resetRingedHere(): void {
  ringedHere.clear();
}

/** True for the channels that auto-accept and therefore need an explicit ring. */
export function isRingChannel(channel: string): boolean {
  return RING_CHANNELS.has(channel.toUpperCase());
}

/** A contact as listContacts() reports it. */
export interface ListedContact {
  contactId: string;
  type: string;
}

/**
 * Pure decision: which listed contacts are new arrivals that should ring.
 *
 * `known` holds every contact id this instance has already seen, and is
 * updated in place. The first look (firstLook=true) only records what is
 * already there, so opening or reloading the Desktop never rings for contacts
 * the agent already has, including the one an instance was just opened for.
 */
export function newArrivals(
  known: Set<string>,
  listed: ListedContact[],
  firstLook: boolean,
): string[] {
  const ring: string[] = [];
  for (const c of listed) {
    if (!c.contactId || known.has(c.contactId)) continue;
    known.add(c.contactId);
    if (!firstLook && isRingChannel(c.type ?? "")) ring.push(c.contactId);
  }
  return ring;
}

/**
 * Claim the ring for a contact across instances. Returns true for the first
 * caller. If localStorage is unavailable it returns true, so this instance
 * still rings (the in-instance guard still applies).
 */
export function claimRing(contactId: string): boolean {
  try {
    const storage = window.localStorage;
    const key = CLAIM_PREFIX + contactId;
    if (storage.getItem(key) !== null) return false;
    storage.setItem(key, String(Date.now()));
  } catch {
    return true;
  }
  pruneClaims();
  return true;
}

// Drop claims older than the TTL so the key space does not grow without bound.
function pruneClaims(): void {
  try {
    const storage = window.localStorage;
    const cutoff = Date.now() - CLAIM_TTL_MS;
    const stale: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !key.startsWith(CLAIM_PREFIX)) continue;
      const at = Number(storage.getItem(key));
      if (!Number.isFinite(at) || at < cutoff) stale.push(key);
    }
    for (const key of stale) storage.removeItem(key);
  } catch {
    // Pruning is housekeeping only.
  }
}

/**
 * Play the ring once. Autoplay rejection goes to onError, never thrown.
 * A fresh element per ring, so a second ring never cuts off the first.
 */
export function playRing(onError: (err: unknown) => void): void {
  try {
    new Audio(RING_SRC).play().catch(onError);
  } catch (err) {
    onError(err);
  }
}

/**
 * Ring once for a contact that just arrived. Called from the listContacts
 * watcher with ids from newArrivals(). Never throws.
 */
export function ringForArrival(
  contactId: string,
  onError: (err: unknown) => void,
): void {
  try {
    if (ringedHere.has(contactId)) return;
    if (!claimRing(contactId)) return;
    ringedHere.add(contactId);
    playRing(onError);
  } catch (err) {
    onError(err);
  }
}
